const API_PREFIX = "/api";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── 401: token missing / expired / server restarted ──────────────────────
// Shared by handleResponse() AND every endpoint below that bypasses it to
// read a raw ArrayBuffer (downloads, multi-receipt extraction, Excel merge).
// Without this shared call those endpoints showed a raw "Download failed" /
// "Request failed" error instead of cleanly forcing the user back to login.
function dispatchSessionExpired() {
  localStorage.removeItem("token");
  localStorage.removeItem("username");
  localStorage.removeItem("vatica_last_activity");
  window.dispatchEvent(new CustomEvent("vatica:session-expired"));
}

async function handleResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : null;

  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }

  if (!response.ok) {
    const message = body?.detail || body?.message || response.statusText || "Request failed";
    throw new Error(message);
  }
  return body;
}

export async function login(role, email, password) {
  return handleResponse(await fetch(`${API_PREFIX}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role, email, password }),
  }));
}

export async function getStatus() {
  return handleResponse(await fetch(`${API_PREFIX}/status`, {
    headers: { ...authHeaders() },
  }));
}

export async function processInvoices(files) {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return handleResponse(await fetch(`${API_PREFIX}/process-invoices`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  }));
}

export async function getAdminClients() {
  return handleResponse(await fetch(`${API_PREFIX}/admin/clients`, {
    headers: { ...authHeaders() },
  }));
}

export async function createAdminClient(client) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/clients`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(client),
  }));
}

export async function toggleClientStatus(clientId) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/clients/${clientId}/toggle-status`, {
    method: "POST",
    headers: { ...authHeaders() },
  }));
}

export async function deleteAdminClient(clientId) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/clients/${clientId}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  }));
}

export async function updateAdminClient(clientId, data) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/clients/${clientId}`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }));
}

export async function revokeClientKey(clientId) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/clients/${clientId}/revoke-key`, {
    method: "POST",
    headers: { ...authHeaders() },
  }));
}

export async function regenerateClientKey(clientId) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/clients/${clientId}/regenerate-key`, {
    method: "POST",
    headers: { ...authHeaders() },
  }));
}

export async function parseVatExcel(file, vatType = "purchases") {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("vat_type", vatType);
  return handleResponse(await fetch(`${API_PREFIX}/parse-vat-excel?vat_type=${vatType}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  }));
}

// ── User / API Key Management (admin only) ──────────────────────────────────

export async function getAdminUsers() {
  return handleResponse(await fetch(`${API_PREFIX}/admin/users`, {
    headers: { ...authHeaders() },
  }));
}

export async function createAdminUser(userData) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/users`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(userData),
  }));
}

export async function pauseAdminUser(userId) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/users/${userId}/pause`, {
    method: "POST",
    headers: { ...authHeaders() },
  }));
}

export async function deleteAdminUser(userId) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/users/${userId}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  }));
}

export async function updateUserApiKey(userId, keyData) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/users/${userId}/api-key`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(keyData),
  }));
}

export async function getBillingData(month = null) {
  const url = month
    ? `${API_PREFIX}/admin/billing?month=${month}`
    : `${API_PREFIX}/admin/billing`;
  return handleResponse(await fetch(url, { headers: { ...authHeaders() } }));
}

export async function getAdminReports() {
  return handleResponse(await fetch(`${API_PREFIX}/admin/reports`, {
    headers: { ...authHeaders() },
  }));
}

export async function getAdminReportData(reportId) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/reports/${reportId}`, {
    headers: { ...authHeaders() },
  }));
}

// ── Admin: Bank Statements (any client, any user) ────────────────────────────

export async function getAdminBankStatements() {
  return handleResponse(await fetch(`${API_PREFIX}/admin/bank-statements`, {
    headers: { ...authHeaders() },
  }));
}

export async function downloadAdminBankExcel(stmtId, filename) {
  const response = await fetch(`${API_PREFIX}/admin/bank-statements/${stmtId}/excel`, {
    headers: { ...authHeaders() },
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Download failed");
  }
  const buffer = await response.arrayBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = filename || "BankStatement.xlsx";
  link.click();
  URL.revokeObjectURL(url);
}

// ── Client Report History ────────────────────────────────────────────────────

export async function getMyReports(clientId = null) {
  const url = clientId != null
    ? `${API_PREFIX}/my-reports?client_id=${clientId}`
    : `${API_PREFIX}/my-reports`;
  return handleResponse(await fetch(url, {
    headers: { ...authHeaders() },
  }));
}

export async function getMyReportData(reportId) {
  return handleResponse(await fetch(`${API_PREFIX}/my-reports/${reportId}/data`, {
    headers: { ...authHeaders() },
  }));
}

export async function saveExcelToHistory(reportId, buffer, filename = "") {
  const formData = new FormData();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  formData.append("file", blob, "report.xlsx");
  if (filename) formData.append("filename", filename);
  return handleResponse(await fetch(`${API_PREFIX}/my-reports/${reportId}/save-excel`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  }));
}

export async function replaceReportExcel(reportId, file) {
  const formData = new FormData();
  formData.append("file", file, file.name);
  return handleResponse(await fetch(`${API_PREFIX}/my-reports/${reportId}/excel`, {
    method: "PUT",
    headers: { ...authHeaders() },
    body: formData,
  }));
}

export function getReportExcelUrl(reportId) {
  return `${API_PREFIX}/my-reports/${reportId}/excel`;
}

export async function downloadSavedExcel(reportId, filename) {
  const response = await fetch(`${API_PREFIX}/my-reports/${reportId}/excel`, {
    headers: { ...authHeaders() },
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Download failed");
  }
  const buffer = await response.arrayBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = filename || "VATICA_Report.xlsx";
  link.click();
  URL.revokeObjectURL(url);
}

// Admin download of a report Excel belonging to ANY client. Hits
// /api/admin/reports/{id}/excel (require_admin, no ownership check) —
// unlike downloadSavedExcel above, which hits the client-facing
// /api/my-reports/{id}/excel route and 403s for admin on reports it
// doesn't itself own.
export async function downloadAdminExcel(reportId, filename) {
  const response = await fetch(`${API_PREFIX}/admin/reports/${reportId}/excel`, {
    headers: { ...authHeaders() },
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Download failed");
  }
  const buffer = await response.arrayBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = filename || "VATICA_Report.xlsx";
  link.click();
  URL.revokeObjectURL(url);
}

// VAT Return / ERS document generator (Input Schedule, Output Schedule,
// VT.01 PDF). Previously this was a raw fetch() inside VatWorkflow.jsx with
// no Authorization header at all and no 401 handling — routed through here
// so it behaves like every other endpoint: sends the token, and a 401
// cleanly bounces the user to login instead of surfacing "Server error 401".
export async function generateVatDocuments(formData) {
  const response = await fetch(`${API_PREFIX}/vat/generate`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || `Server error ${response.status}`);
  }
  return response.json();
}

export async function deleteMyReport(reportId) {
  return handleResponse(await fetch(`${API_PREFIX}/my-reports/${reportId}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  }));
}

export async function deleteAdminReport(reportId) {
  return handleResponse(await fetch(`${API_PREFIX}/admin/reports/${reportId}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  }));
}

// ── Client Management ─────────────────────────────────────────────────────────

export async function getMyClients() {
  return handleResponse(await fetch(`${API_PREFIX}/my-clients`, {
    headers: { ...authHeaders() },
  }));
}

export async function createMyClient(clientData) {
  return handleResponse(await fetch(`${API_PREFIX}/my-clients`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(clientData),
  }));
}

export async function updateMyClient(clientId, data) {
  return handleResponse(await fetch(`${API_PREFIX}/my-clients/${clientId}`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }));
}

export async function deleteMyClient(clientId) {
  return handleResponse(await fetch(`${API_PREFIX}/my-clients/${clientId}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  }));
}

export async function getClientReports(clientId) {
  return handleResponse(await fetch(`${API_PREFIX}/my-clients/${clientId}/reports`, {
    headers: { ...authHeaders() },
  }));
}

/**
 * Get current user's own page usage breakdown — for the Usage tab transparency page.
 * @param {string|null} month  YYYY-MM — defaults to current month
 */
export async function getMyUsage(month = null) {
  const url = month
    ? `${API_PREFIX}/my-usage?month=${month}`
    : `${API_PREFIX}/my-usage`;
  return handleResponse(await fetch(url, { headers: { ...authHeaders() } }));
}

/**
 * Upload sales invoice PDFs/images → AI extraction → returns structured records.
 * @param {File[]} files
 * @param {number|null} clientId
 */
export async function processSalesInvoices(files, clientId = null) {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  if (clientId != null) formData.append("client_id", String(clientId));
  return handleResponse(await fetch(`${API_PREFIX}/process-sales-invoices`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  }));
}

export async function saveSalesSnapshot(salesRecords = [], clientId = null, clientName = "", month = null) {
  return handleResponse(await fetch(`${API_PREFIX}/save-sales-snapshot`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      sales_records: salesRecords,
      client_id:     clientId || null,
      client_name:   clientName || "",
      month:         month || new Date().toISOString().substring(0, 7),
    }),
  }));
}

export async function tagReportToClient(reportId, clientId) {
  return handleResponse(await fetch(`${API_PREFIX}/my-reports/${reportId}/tag-client`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ client_id: clientId }),
  }));
}

export async function saveSalesExcel(buffer, clientId = null, month = null, filename = "", reportId = null) {
  const formData = new FormData();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  formData.append("file", blob, "sales_report.xlsx");
  if (clientId != null) formData.append("client_id", String(clientId));
  if (month)            formData.append("month",     month);
  if (filename)         formData.append("filename",  filename);
  if (reportId)         formData.append("report_id", reportId);
  return handleResponse(await fetch(`${API_PREFIX}/save-sales-excel`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  }));
}

// ══════════════════════════════════════════════════════════════════════════════
// BANK STATEMENTS  (V5 NEW)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Upload a bank statement PDF for extraction.
 * @param {File}   pdfFile     - the PDF file from the input element
 * @param {string} bank        - "standard_bank" | "standard_bank_loan" | "fnb" | "eswatini_bank" | "nedbank" | "nedbank_current" | "sbs_loan"
 * @param {string} accountType - "current" | "loan"
 * @param {number|null} clientId
 * @returns {Promise<object>}  extraction result metadata
 */
export async function extractBankStatement(pdfFile, bank, accountType, clientId = null) {
  const formData = new FormData();
  formData.append("pdf_file",     pdfFile);
  formData.append("bank",         bank);
  formData.append("account_type", accountType);
  if (clientId != null) formData.append("client_id", String(clientId));
  return handleResponse(await fetch(`${API_PREFIX}/extract-bank-statement`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  }));
}

/**
 * List bank statement extractions for the current user.
 * @param {number|null} clientId  — optional filter by client
 */
export async function getMyBankStatements(clientId = null) {
  const url = clientId
    ? `${API_PREFIX}/my-bank-statements?client_id=${clientId}`
    : `${API_PREFIX}/my-bank-statements`;
  return handleResponse(await fetch(url, { headers: { ...authHeaders() } }));
}

/**
 * Build a bank-statement Excel filename from the original uploaded PDF's
 * name (the `filename_hint` recorded at extraction time), not from the
 * bank/extractor label — e.g. "March 2026 Checking.pdf" downloads as
 * "March 2026 Checking.xlsx", not "BankStatement_FNB_142.xlsx".
 * Mirrors _filename_from_hint() in api_bank_endpoints.py — keep in sync.
 * @param {string} hint      - filename_hint from the bank statement row
 * @param {string} fallback  - name to use if hint is missing/empty
 * @param {string} suffix    - inserted before the extension (e.g. "_WithInvoices")
 */
export function bankStatementFilename(hint, fallback, suffix = "") {
  const base = (hint || "").split(/[\\/]/).pop() || "";
  const stem = base.replace(/\.pdf$/i, "").trim();
  return stem ? `${stem}${suffix}.xlsx` : fallback;
}

/**
 * Download the extracted bank statement Excel.
 * Triggers a browser download.
 * @param {number} stmtId
 * @param {string} filename  - suggested filename for the download
 */
export async function downloadBankStatementExcel(stmtId, filename) {
  const response = await fetch(`${API_PREFIX}/my-bank-statements/${stmtId}/excel`, {
    headers: { ...authHeaders() },
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Download failed");
  }
  const buffer = await response.arrayBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = filename || `BankStatement_${stmtId}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Delete a bank statement extraction record.
 * @param {number} stmtId
 */
export async function deleteMyBankStatement(stmtId) {
  return handleResponse(await fetch(`${API_PREFIX}/my-bank-statements/${stmtId}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  }));
}

/**
 * Inject invoices into a bank statement Excel and download the merged file.
 * @param {number} stmtId        - bank statement record id
 * @param {File}   invoicesFile  - the client's VAT Purchases Excel
 * @param {string} filename      - suggested output filename
 */
export async function injectAndDownload(stmtId, invoicesFile, filename) {
  const formData = new FormData();
  formData.append("invoices_file", invoicesFile);
  const response = await fetch(`${API_PREFIX}/my-bank-statements/${stmtId}/inject-invoices`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Injection failed");
  }
  const buffer = await response.arrayBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = filename || `BankStatement_WithInvoices_${stmtId}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Get bank statement extractions linked to a specific client (for ClientProfile).
 * @param {number} clientId
 */
export async function getClientBankStatements(clientId) {
  return handleResponse(await fetch(`${API_PREFIX}/my-clients/${clientId}/bank-statements`, {
    headers: { ...authHeaders() },
  }));
}

/**
 * Merge 2+ already-extracted bank statements into one consolidated Excel
 * workbook (deterministic — no AI). Triggers a browser download and
 * returns the merge diagnostics read from the response headers.
 * @param {number[]}      statementIds
 * @param {string}        filename     - suggested output filename
 * @param {number|null}   taxYear      - optional Eswatini tax year start (e.g. 2024)
 * @returns {Promise<{nStatements:number, nTransactions:number, nDuplicates:number,
 *                     nTransfers:number, missingMonths:string[], taxYearLabel:string}>}
 */
export async function mergeBankStatements(statementIds, filename, taxYear = null) {
  const body = { statement_ids: statementIds };
  if (taxYear) body.tax_year = taxYear;

  const response = await fetch(`${API_PREFIX}/merge-bank-statements`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body:    JSON.stringify(body),
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.detail || "Merge failed");
  }

  const meta = {
    nStatements:   parseInt(response.headers.get("X-Merge-Statements")   || "0", 10),
    nTransactions: parseInt(response.headers.get("X-Merge-Transactions") || "0", 10),
    nDuplicates:   parseInt(response.headers.get("X-Merge-Duplicates")   || "0", 10),
    nTransfers:    parseInt(response.headers.get("X-Merge-Transfers")    || "0", 10),
    missingMonths: (response.headers.get("X-Merge-Missing") || "")
      .split(",")
      .map(m => m.trim())
      .filter(m => m && m !== "none"),
    taxYearLabel:  response.headers.get("X-Merge-Tax-Year") || "",
  };

  const buffer = await response.arrayBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = filename || "VATICA_Merged.xlsx";
  link.click();
  URL.revokeObjectURL(url);

  return meta;
}

/**
 * Merge 2+ manually-uploaded VATICA-generated Excel statement files into
 * one consolidated workbook — for when the user wants to bring their own
 * files instead of picking from system history (e.g. the system copy is
 * missing, or the files are from another device). No statement IDs or
 * JSON sidecars involved — each file's data sheet is parsed directly on
 * the backend. Triggers a browser download; same diagnostics shape as
 * mergeBankStatements().
 * @param {File[]}        files     - 2+ .xlsx files produced by VATICA extractors
 * @param {string}        filename  - suggested output filename
 * @param {number|null}   taxYear   - optional Eswatini tax year start (e.g. 2024)
 * @param {number|null}   clientId  - optional, used only to namespace the output folder
 * @returns {Promise<{nStatements:number, nTransactions:number, nDuplicates:number,
 *                     nTransfers:number, missingMonths:string[], taxYearLabel:string}>}
 */
export async function mergeBankStatementsFromExcel(files, filename, taxYear = null, clientId = null) {
  const formData = new FormData();
  files.forEach(f => formData.append("files", f));
  if (taxYear)  formData.append("tax_year", String(taxYear));
  if (clientId != null) formData.append("client_id", String(clientId));

  const response = await fetch(`${API_PREFIX}/merge-bank-statements-from-excel`, {
    method:  "POST",
    headers: { ...authHeaders() },
    body:    formData,
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.detail || "Merge failed");
  }

  const meta = {
    nStatements:   parseInt(response.headers.get("X-Merge-Statements")   || "0", 10),
    nTransactions: parseInt(response.headers.get("X-Merge-Transactions") || "0", 10),
    nDuplicates:   parseInt(response.headers.get("X-Merge-Duplicates")   || "0", 10),
    nTransfers:    parseInt(response.headers.get("X-Merge-Transfers")    || "0", 10),
    missingMonths: (response.headers.get("X-Merge-Missing") || "")
      .split(",")
      .map(m => m.trim())
      .filter(m => m && m !== "none"),
    taxYearLabel:  response.headers.get("X-Merge-Tax-Year") || "",
  };

  const buffer = await response.arrayBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = filename || "VATICA_Merged.xlsx";
  link.click();
  URL.revokeObjectURL(url);

  return meta;
}

// ══════════════════════════════════════════════════════════════════════════════
// MULTI-RECEIPT EXTRACTORS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Upload one or more multi-receipt purchases PDFs → AI extraction → returns
 * one combined Excel ArrayBuffer covering every receipt from every file.
 * @param {File[]|File} pdfFiles     a single File, or an array of Files
 * @param {string}      clientName   used in the filename
 * @returns {Promise<ArrayBuffer>}
 */
export async function processMultiReceiptsPurchases(pdfFiles, clientName = "", clientId = null) {
  const files = Array.isArray(pdfFiles) ? pdfFiles : [pdfFiles];
  const formData = new FormData();
  files.forEach(f => formData.append("pdf_files", f));
  if (clientName) formData.append("client_name", clientName);
  if (clientId != null) formData.append("client_id", String(clientId));
  const response = await fetch(`${API_PREFIX}/process-multi-receipts-purchases`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Multi-receipt purchases processing failed");
  }
  return response.arrayBuffer();
}

/**
 * Upload one or more multi-receipt sales PDFs → AI extraction → returns
 * one combined Excel ArrayBuffer covering every receipt from every file.
 * @param {File[]|File} pdfFiles
 * @param {string}      clientName
 * @returns {Promise<ArrayBuffer>}
 */
export async function processMultiReceiptsSales(pdfFiles, clientName = "", clientId = null) {
  const files = Array.isArray(pdfFiles) ? pdfFiles : [pdfFiles];
  const formData = new FormData();
  files.forEach(f => formData.append("pdf_files", f));
  if (clientName) formData.append("client_name", clientName);
  if (clientId != null) formData.append("client_id", String(clientId));
  const response = await fetch(`${API_PREFIX}/process-multi-receipts-sales`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Multi-receipt sales processing failed");
  }
  return response.arrayBuffer();
}

// ══════════════════════════════════════════════════════════════════════════════
// Z-REPORT EXTRACTOR
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Upload a Z-Report PDF (till end-of-day reports) → AI extraction → returns Excel ArrayBuffer.
 * Same (pdf_file, client_name, client_id) -> Excel contract as processMultiReceiptsSales,
 * since /api/process-z-reports mirrors /api/process-multi-receipts-sales on the backend.
 * @param {File}        pdfFile
 * @param {string}      clientName   used in the filename and, if a clientId is given, linked to that client
 * @param {number|null} clientId
 * @param {string}      [tin]        optional — applied to every row in the batch
 * @param {string}      [customer]   optional — applied to every row in the batch
 * @returns {Promise<ArrayBuffer>}
 */
export async function processZReports(pdfFile, clientName = "", clientId = null, tin = "", customer = "") {
  const formData = new FormData();
  formData.append("pdf_file", pdfFile);
  if (clientName) formData.append("client_name", clientName);
  if (clientId != null) formData.append("client_id", String(clientId));
  if (tin) formData.append("tin", tin);
  if (customer) formData.append("customer", customer);
  const response = await fetch(`${API_PREFIX}/process-z-reports`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Z-Report processing failed");
  }
  return response.arrayBuffer();
}

// ══════════════════════════════════════════════════════════════════════════════
// QUICKBOOKS VAT REPORT
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Upload a QuickBooks VAT report CSV → returns JSON with two base64-encoded Excel files.
 * @param {File} csvFile
 * @returns {Promise<{outputs_b64: string, inputs_b64: string, filename_outputs: string, filename_inputs: string}>}
 */
export async function processQuickbooksReport(csvFile) {
  const formData = new FormData();
  formData.append("csv_file", csvFile);
  return handleResponse(await fetch(`${API_PREFIX}/process-quickbooks-report`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  }));
}

// ══════════════════════════════════════════════════════════════════════════════
// EXCEL MERGE
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Merge two Excel Blobs/Files into one workbook → returns merged ArrayBuffer.
 * @param {Blob|File} blob1       first workbook
 * @param {Blob|File} blob2       second workbook
 * @param {string}    filename    suggested output filename
 * @returns {Promise<ArrayBuffer>}
 */
export async function mergeExcelFiles(blob1, blob2, filename = "Merged_Report.xlsx") {
  const formData = new FormData();
  formData.append("file1", blob1, "workbook1.xlsx");
  formData.append("file2", blob2, "workbook2.xlsx");
  formData.append("output_filename", filename);
  const response = await fetch(`${API_PREFIX}/merge-excel`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired — please sign in again.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || "Excel merge failed");
  }
  return response.arrayBuffer();
}

/* ─────────────────────────────────────────────────────────
   CONNECTORS — pull reports from Xero / QuickBooks / Sage / Zoho
───────────────────────────────────────────────────────── */

/**
 * Start the OAuth flow for a provider. Returns {authorize_url} — the
 * caller must do a full page navigation to it (window.location.href = ...),
 * not a fetch, since the provider needs to render its own login screen.
 * @param {"xero"|"quickbooks"|"sage"|"zoho"} provider
 * @param {number|null} clientId  tax_agent_clients.id to tag this connection to
 */
export async function connectProvider(provider, clientId = null) {
  const url = clientId != null
    ? `${API_PREFIX}/connectors/${provider}/connect?client_id=${clientId}`
    : `${API_PREFIX}/connectors/${provider}/connect`;
  return handleResponse(await fetch(url, { headers: { ...authHeaders() } }));
}

/**
 * List this tax agent's connected accounts, optionally filtered to one client.
 * @param {number|null} clientId
 */
export async function getConnections(clientId = null) {
  const url = clientId != null
    ? `${API_PREFIX}/connectors?client_id=${clientId}`
    : `${API_PREFIX}/connectors`;
  return handleResponse(await fetch(url, { headers: { ...authHeaders() } }));
}

export async function disconnectProvider(connectionId) {
  return handleResponse(await fetch(`${API_PREFIX}/connectors/${connectionId}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  }));
}

/**
 * Pull a report from a connected account. If the connection is tagged to a
 * client, the backend also links it into that client's Reports list — no
 * separate "save" step needed.
 * @param {number} connectionId
 * @param {"profit_and_loss"|"balance_sheet"|"trial_balance"} reportType
 * @param {string|null} startDate  YYYY-MM-DD
 * @param {string|null} endDate    YYYY-MM-DD
 */
export async function pullConnectorReport(connectionId, reportType, startDate = null, endDate = null) {
  const params = new URLSearchParams({ report_type: reportType });
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  return handleResponse(await fetch(`${API_PREFIX}/connectors/${connectionId}/pull-report?${params}`, {
    method: "POST",
    headers: { ...authHeaders() },
  }));
}

/**
 * Sage doesn't use OAuth — the client gives their own Sage username,
 * password, and Sage-issued API key directly. This validates them and
 * returns the companies they can access, without saving anything yet.
 */
export async function sageLogin(username, password, apiKey) {
  return handleResponse(await fetch(`${API_PREFIX}/connectors/sage/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ username, password, api_key: apiKey }),
  }));
}

/** Persists the chosen Sage company after sageLogin() has validated it. */
export async function sageSave(username, password, apiKey, companyId, companyName, clientId = null) {
  return handleResponse(await fetch(`${API_PREFIX}/connectors/sage/save`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({
      username, password, api_key: apiKey,
      company_id: companyId, company_name: companyName, client_id: clientId,
    }),
  }));
}
